import type { TimelineGridPrecisionOptionValue } from '@ardoq/api-types';
import type { TimeRange } from '@ardoq/graph';
import { formatDateISO, isDateValid } from '@ardoq/date-time';
import { TIMELINES_GRID_PRECISION_OPTIONS } from './consts';
import { isManualGridOption } from './grid/calculations';
import { isGridOptionDisabled } from './grid/utils';
import type {
  CollapsibleIds,
  TimelineCollapsibleTarget,
  TimelineDataDomain,
  TimelineItemModel,
  TimelineSettingsBounds,
  TimelineViewModelData,
} from './types';

const isGridPrecisionAvailable = (
  chartDomainRange: TimeRange,
  gridPrecision: TimelineGridPrecisionOptionValue
): boolean =>
  !isManualGridOption(gridPrecision) ||
  !isGridOptionDisabled({ chartDomainRange, gridPrecision });

const toAvailableGridPrecisions = (
  chartDomainRange: TimeRange
): TimelineGridPrecisionOptionValue[] =>
  TIMELINES_GRID_PRECISION_OPTIONS.filter(([gridPrecision]) =>
    isGridPrecisionAvailable(chartDomainRange, gridPrecision)
  ).map(([gridPrecision]) => gridPrecision);

const toDataDomain = ([start, end]: [Date, Date]): TimelineDataDomain | null =>
  isDateValid(start) && isDateValid(end)
    ? [formatDateISO(start), formatDateISO(end)]
    : null;

const toCollapsibleTarget = ({
  rawItemIdPath,
  itemLabels,
  treeDepth,
}: TimelineItemModel): TimelineCollapsibleTarget => ({
  id: rawItemIdPath,
  label: itemLabels.mainLabel ?? '',
  depth: treeDepth,
});

/**
 * The bounds the AI view settings context publishes. The JSDoc on
 * `SettableTimelineViewSettings` and `PersistedCollapsedState` points the model
 * at these by name, so a key removed here leaves those descriptions naming
 * something absent.
 */
export const TIMELINE_AI_VIEW_STATE_KEYS = [
  'canSetMilestoneFields',
  'milestoneFields',
  'canSetPhaseFields',
  'phaseFields',
  'availableGridPrecisions',
  'dataDomain',
  'canSetCollapsedGroups',
  'collapsibleGroupTargets',
  'canSetCollapsedFieldData',
  'collapsibleFieldDataTargets',
] as const satisfies readonly (keyof TimelineSettingsBounds)[];

const toCollapsibleTargetsForIds = (
  viewModelData: TimelineItemModel[],
  { collapsible }: CollapsibleIds
): TimelineCollapsibleTarget[] =>
  viewModelData
    .filter(({ rawItemIdPath }) => collapsible.has(rawItemIdPath))
    .map(toCollapsibleTarget);

type TimelineSettingsBoundsSource = {
  timelineViewModelData: TimelineViewModelData;
  /** The window the chart can pan over, not the extent of the data. */
  availableExpandedDomain: TimeRange;
};

export const toTimelineSettingsBounds = ({
  timelineViewModelData,
  availableExpandedDomain,
}: TimelineSettingsBoundsSource): TimelineSettingsBounds => {
  const {
    timelineViewFieldsRegistry: { milestoneFields, timeSpanFields },
    viewModelDataDomain,
    viewModelData,
    collapsibleGroups,
    collapsibleFieldDataIds,
  } = timelineViewModelData;
  const availableGridPrecisions = toAvailableGridPrecisions(
    availableExpandedDomain
  );
  const collapsibleGroupTargets = toCollapsibleTargetsForIds(
    viewModelData,
    collapsibleGroups
  );
  const collapsibleFieldDataTargets = toCollapsibleTargetsForIds(
    viewModelData,
    collapsibleFieldDataIds
  );

  return {
    milestoneFields,
    canSetMilestoneFields: milestoneFields.length > 0,
    phaseFields: timeSpanFields,
    canSetPhaseFields: timeSpanFields.length > 0,
    availableGridPrecisions,
    dataDomain: toDataDomain(viewModelDataDomain),
    collapsibleGroupTargets,
    canSetCollapsedGroups: collapsibleGroupTargets.length > 0,
    collapsibleFieldDataTargets,
    canSetCollapsedFieldData: collapsibleFieldDataTargets.length > 0,
  };
};
